import { useNavigate } from 'react-router-dom';
import { useActor, ACTORS } from '@/context/ActorContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Building2, Users, School, Package, GraduationCap, ArrowRight, 
  BookOpen, BarChart3, ShoppingCart, Truck
} from 'lucide-react';
import { ENTITY_LOGOS } from '@/lib/assets';

const actorDetails = {
  moe_hq: {
    icon: Building2,
    logoKey: 'moe',
    features: ['Curation Approval', 'Quote Management', 'School Monitoring', 'Reports & Analytics'],
    stats: { label: 'Schools', value: '287' }
  },
  civica: {
    icon: Users,
    logoKey: 'civica',
    features: ['Curation Management', 'Quote Invitations', 'Delivery Tracking', 'Invoicing'],
    stats: { label: 'Titles Managed', value: '228' }
  },
  school: {
    icon: School,
    logoKey: 'rgs',
    features: ['Budget Setup', 'Title Selection', 'Order Tracking', 'Delivery Acknowledgement'],
    stats: { label: 'Budget', value: '$3,000' }
  },
  supplier: {
    icon: Package,
    logoKey: 'opentrolley',
    features: ['Quote Response', 'PO Fulfilment', 'Dispatch Updates', 'Order History'],
    stats: { label: 'Active POs', value: '3' }
  },
  mk: {
    icon: GraduationCap,
    logoKey: 'mk',
    features: ['View Book Order', 'Track Delivery', 'Acknowledge Receipt', 'Report Issues'],
    stats: { label: 'Books', value: '20' }
  }
};

export default function ActorSelection() {
  const navigate = useNavigate();
  const { switchActor } = useActor();

  const handleSelectActor = (actorId) => {
    switchActor(actorId);
    navigate(ACTORS[actorId].basePath);
  };

  return (
    <div className="min-h-screen bg-[#F0F2F7]">
      {/* Header */}
      <header className="bg-[#1A3C6E] text-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold">SLF CDS Acquisition Module</h1>
              <p className="text-white/70 text-sm">Singapore Library Framework | MOE School Libraries</p>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center gap-2 text-white/60 text-sm mb-1">
                <School className="w-4 h-4" />
                Schools
              </div>
              <div className="text-2xl font-bold">287</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center gap-2 text-white/60 text-sm mb-1">
                <GraduationCap className="w-4 h-4" />
                MOE Kindergartens
              </div>
              <div className="text-2xl font-bold">58</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center gap-2 text-white/60 text-sm mb-1">
                <BookOpen className="w-4 h-4" />
                Curated Titles
              </div>
              <div className="text-2xl font-bold">228</div>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center gap-2 text-white/60 text-sm mb-1">
                <BarChart3 className="w-4 h-4" />
                Total Budget
              </div>
              <div className="text-2xl font-bold">$861K</div>
            </div>
          </div>
        </div>
      </header>

      {/* Actor Selection Grid */}
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">Select Portal</h2>
          <p className="text-slate-600">Choose your role to access the appropriate dashboard and features.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(ACTORS).map(([id, actor]) => {
            const details = actorDetails[id];
            const Icon = details.icon;
            const logoUrl = ENTITY_LOGOS[details.logoKey];
            
            return (
              <Card 
                key={id}
                className="group hover:shadow-lg transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-[#2E5F8A]/30"
                onClick={() => handleSelectActor(id)}
                data-testid={`actor-card-${id}`}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
                      <img 
                        src={logoUrl} 
                        alt={actor.shortName}
                        className="w-full h-full object-contain p-1"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div 
                        className="w-full h-full hidden items-center justify-center text-white"
                        style={{ backgroundColor: actor.color }}
                      >
                        <Icon className="w-8 h-8" />
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">{details.stats.label}</div>
                      <div className="text-lg font-semibold text-slate-900">{details.stats.value}</div>
                    </div>
                  </div>
                  <CardTitle className="mt-4 font-heading">{actor.shortName}</CardTitle>
                  <CardDescription>{actor.role}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    {details.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#2E5F8A]" />
                        {feature}
                      </div>
                    ))}
                  </div>
                  
                  <div className="pt-4 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-slate-900">{actor.userName}</div>
                        <div className="text-xs text-slate-500">{actor.userRole}</div>
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-[#1A3C6E] hover:bg-[#142E54] group-hover:translate-x-1 transition-transform"
                        data-testid={`enter-${id}-btn`}
                      >
                        Enter
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Programme Info */}
        <div className="mt-12 bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Current Programme Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
              <div>
                <div className="text-sm font-medium text-slate-900">Pilot C1</div>
                <div className="text-xs text-slate-500">Complete (Aug-Oct 2026)</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-amber-500 animate-pulse" />
              <div>
                <div className="text-sm font-medium text-slate-900">Pilot C2</div>
                <div className="text-xs text-slate-500">In Progress (Nov 2026)</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-amber-500" />
              <div>
                <div className="text-sm font-medium text-slate-900">CDS Cycle 1 2028</div>
                <div className="text-xs text-slate-500">Selection Phase</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-slate-300" />
              <div>
                <div className="text-sm font-medium text-slate-900">CDS Cycle 2 2028</div>
                <div className="text-xs text-slate-500">Upcoming</div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-slate-500">
              © 2026 Ministry of Education, Singapore. All rights reserved.
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <a href="#" className="hover:text-slate-900">Help</a>
              <a href="#" className="hover:text-slate-900">Contact</a>
              <a href="#" className="hover:text-slate-900">Privacy Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
